import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { motion } from 'framer-motion';
import {
  Bed,
  Bath,
  Square,
  MapPin,
  Share2,
  ArrowLeft,
  CheckCircle,
  Loader,
  Car,
  Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/lib/utils';
import { initializeTracking, trackView, trackPageView } from '@/lib/tracking';
import type { Property, PropertyImage, User, PropertyCategory } from '@/types';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import ImageGallery from '@/components/details/ImageGallery';
import ItemDescription from '@/components/details/ItemDescription';
import ContactSidebar from '@/components/details/ContactSidebar';

export default function PropertyDetailsPage() {
  const { slug, propertyId } = useParams();
  const [property, setProperty] = useState<Property | null>(null);
  const [corretor, setCorretor] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareSupported, setShareSupported] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    setShareSupported(!!navigator.share && window.isSecureContext);

    const fetchPropertyDetails = async () => {
      try {
        if (!propertyId) {
          setError("ID do imóvel não encontrado");
          return;
        }

        // Fetch property details with images
        const { data: propertyData, error: propertyError } = await supabase
          .from('properties')
          .select(`
            *,
            property_images (
              url
            )
          `)
          .eq('id', propertyId)
          .single();

        if (propertyError) throw propertyError;
        if (!propertyData) {
          setError("Imóvel não encontrado");
          return;
        }

        setProperty(propertyData);

        // Fetch corretor details
        const { data: corretorData, error: corretorError } = await supabase
          .from('users')
          .select('*')
          .eq('id', propertyData.user_id)
          .single();

        if (corretorError) throw corretorError;
        setCorretor(corretorData);

        // Apply corretor's theme settings
        if (corretorData) {
          // Set theme based on broker's preference
          if (corretorData.theme) {
            document.documentElement.className = corretorData.theme;
          }
          
          // Apply broker's custom colors safely
          try {
            const root = document.documentElement;
            root.style.setProperty('--broker-primary', corretorData.primary_color || '#0f172a');
            root.style.setProperty('--broker-primary-foreground', corretorData.primary_foreground || '#f8fafc');
            root.style.setProperty('--broker-accent', corretorData.accent_color || '#6366f1');
            root.style.setProperty('--broker-accent-foreground', corretorData.accent_foreground || '#ffffff');
            document.body.classList.add('broker-theme');
          } catch (e) {
            console.error('Error applying custom colors:', e);
          }

          // Initialize tracking
          await initializeTracking(corretorData.id);
          
          // Track page view with enhanced data
          await trackPageView(corretorData.id, {
            page_title: `${propertyData.title} | ${corretorData.name}`,
            page_location: window.location.href,
            content_group1: 'property_detail'
          });

          // Track property view with enhanced analytics
          await trackView(propertyId, 'property', {
            itemId: propertyId,
            itemType: 'property',
            itemTitle: propertyData.title,
            itemPrice: propertyData.price,
            userId: corretorData.id,
            source: document.referrer || 'direct'
          });
        }

      } catch (err) {
        console.error('Error fetching property details:', err);
        setError("Erro ao carregar os dados do imóvel");
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyDetails();

    // Cleanup function
    return () => {
      try {
        document.documentElement.style.removeProperty('--broker-primary');
        document.documentElement.style.removeProperty('--broker-primary-foreground');
        document.documentElement.style.removeProperty('--broker-accent');
        document.documentElement.style.removeProperty('--broker-accent-foreground');
        document.body.classList.remove('broker-theme');
        document.documentElement.className = theme; // Restore original theme
      } catch (e) {
        console.error('Error cleaning up styles:', e);
      }
    };
  }, [propertyId, theme]);

  const handleShareClick = async () => {
    const shareUrl = window.location.href;
    const shareTitle = property?.title || 'Imóvel';
    const shareText = `Confira este imóvel: ${property?.title}`;

    try {
      if (shareSupported) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        toast.success('Compartilhado com sucesso!');
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copiado para a área de transferência');
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Link copiado para a área de transferência');
        } catch (err) {
          toast.error('Não foi possível copiar o link');
        }
      }
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'venda':
        return <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm border-primary/20">Venda</Badge>;
      case 'aluguel':
        return <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm border-primary/20">Aluguel</Badge>;
      case 'venda_aluguel':
        return <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm border-primary/20">Venda/Aluguel</Badge>;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'vendido':
        return <Badge variant="destructive" className="bg-destructive/90 backdrop-blur-sm">Vendido</Badge>;
      case 'alugado':
        return <Badge variant="destructive" className="bg-destructive/90 backdrop-blur-sm">Alugado</Badge>;
      default:
        return null;
    }
  };

  const getCategoryBadge = (category?: PropertyCategory) => {
    if (!category) return null;
    
    return (
      <Badge variant="outline" className="bg-background/80 backdrop-blur-sm border-primary/20">
        {category}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !property || !corretor) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4">
        <p className="text-lg text-muted-foreground">
          {error || "Erro ao carregar os dados do imóvel"}
        </p>
        <Button asChild>
          <Link to={`/${slug}`}>Voltar para a vitrine</Link>
        </Button>
      </div>
    );
  }

  // Create array of images, using featured image or default if no images
  const galleryImages = property.property_images?.length 
    ? property.property_images.map(img => img.url)
    : [property.featured_image_url || "https://images.pexels.com/photos/1396132/pexels-photo-1396132.jpeg"];

  return (
    <div className="flex-1">
      {/* Back button */}
      <div className="container mx-auto px-4 py-4">
        <Button variant="ghost" asChild className="pl-0 hover:pl-1 transition-all">
          <Link to={`/${slug}`} className="flex items-center">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para a vitrine
          </Link>
        </Button>
      </div>

      {/* Property Information */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-start gap-8">
            {/* Property Details */}
            <motion.div 
              className="flex-1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex gap-2 mb-3">
                    {getTypeBadge(property.type)}
                    {getCategoryBadge(property.category)}
                    {property.status !== 'disponivel' && getStatusBadge(property.status)}
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold">{property.title}</h1>
                  
                  {property.address && (
                    <div className="flex items-center text-muted-foreground mt-2">
                      <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span>{property.address}, {property.city} - {property.state}</span>
                    </div>
                  )}
                </div>

                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={handleShareClick}
                >
                  <Share2 className="h-5 w-5" />
                </Button>
              </div>
              
              {/* Price information */}
              <div className="mt-6 mb-8">
                <div className="text-3xl font-bold text-primary">
                  {formatCurrency(property.price)}
                </div>
                {property.rent_price && (
                  <div className="text-xl text-muted-foreground mt-1">
                    Aluguel: {formatCurrency(property.rent_price)}/mês
                  </div>
                )}

                {/* Featured Offer */}
                {property.featured_offer_price && property.featured_offer_installment && (
                  <div className="mt-4 p-4 bg-primary/10 rounded-lg">
                    <h3 className="text-lg font-semibold text-primary mb-2">
                      Oferta Especial
                    </h3>
                    <div className="space-y-2">
                      <p className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-primary" />
                        Entrada de {formatCurrency(property.featured_offer_price)}
                      </p>
                      <p className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-primary" />
                        Parcelas de {formatCurrency(property.featured_offer_installment)}
                      </p>
                      {property.featured_offer_description && (
                        <p className="text-sm text-muted-foreground mt-2">
                          {property.featured_offer_description}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Property Stats Grid */}
              <div className="grid grid-cols-5 gap-4 p-4 bg-muted/20 rounded-lg mb-8">
                <div className="flex flex-col items-center justify-center">
                  <Square className="h-6 w-6 text-primary mb-1" />
                  <span className="font-semibold text-lg">{property.area}m²</span>
                  <span className="text-sm text-muted-foreground">Área</span>
                </div>
                <div className="flex flex-col items-center justify-center">
                  <Bed className="h-6 w-6 text-primary mb-1" />
                  <span className="font-semibold text-lg">{property.bedrooms}</span>
                  <span className="text-sm text-muted-foreground">
                    {property.bedrooms === 1 ? 'Quarto' : 'Quartos'}
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center">
                  <Bath className="h-6 w-6 text-primary mb-1" />
                  <span className="font-semibold text-lg">{property.bathrooms}</span>
                  <span className="text-sm text-muted-foreground">
                    {property.bathrooms === 1 ? 'Banheiro' : 'Banheiros'}
                  </span>
                </div>
                {property.suites > 0 && (
                  <div className="flex flex-col items-center justify-center">
                    <Crown className="h-6 w-6 text-primary mb-1" />
                    <span className="font-semibold text-lg">{property.suites}</span>
                    <span className="text-sm text-muted-foreground">
                      {property.suites === 1 ? 'Suíte' : 'Suítes'}
                    </span>
                  </div>
                )}
                <div className="flex flex-col items-center justify-center">
                  <Car className="h-6 w-6 text-primary mb-1" />
                  <span className="font-semibold text-lg">{property.parking_spaces}</span>
                  <span className="text-sm text-muted-foreground">
                    {property.parking_spaces === 1 ? 'Vaga' : 'Vagas'}
                  </span>
                </div>
              </div>

              {/* Photo Gallery */}
              <ImageGallery 
                images={galleryImages}
                title={property.title}
                videoUrl={property.video_url}
              />

              {/* Description */}
              <ItemDescription description={property.description} />
            </motion.div>
            
            {/* Broker Information Sidebar */}
            <motion.div 
              className="w-full md:w-80 lg:w-96"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <ContactSidebar
                corretor={corretor}
                itemId={property.id}
                itemTitle={property.title}
                itemType="imóvel"
                createdAt={property.created_at}
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}